import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, MenuTrigger, mergeClasses } from '@coreai/fluentui-react';
import { Add16Regular, ChevronDown12Regular } from '@fluentui/react-icons';
import { paths } from '@/routes';
import { extractWorkflowName } from '@/types/logicApps';
import { usePillStyles } from './pillStyles';
import { useMenuList } from './useMenuList';
import { PillMenu } from './PillMenu';
import { useNodeEditorStore } from '@/stores/nodeEditorStore';
import { blockHeaderActionForCodeIssue } from './codeNavigationGuard';

/**
 * Logical workflow status used by the leaf pill's status dot.
 * We map runtime/last-run states down to these four buckets so the dot
 * stays glanceable. Detailed status lives in the Monitoring view.
 */
export type WorkflowStatus = 'running' | 'succeeded' | 'failed' | 'draft' | 'idle';

interface WorkflowSummary {
  /** Workflow resource name (may be prefixed with the app name; rendered name is extracted). */
  name: string;
  /** Optional logical status to drive the dot colour in the dropdown. */
  status?: WorkflowStatus;
}

interface WorkflowLeafPillProps {
  /** Current location (Azure region) name from URL params. */
  locationName: string;
  /** Current project name from URL params. */
  projectName: string;
  /** Current app name from URL params. */
  appName: string;
  /** Current workflow name from URL params. May contain an app/workflow prefix. */
  workflowName: string;
  /** Workflows in the current app. */
  workflows: readonly WorkflowSummary[];
  /** Logical status of the current workflow. */
  currentStatus?: WorkflowStatus | undefined;
  /** Tenant id for navigation. */
  tenantId?: string | null | undefined;
  /** When provided (and `canCreate` is true), the dropdown renders a
   *  "+ Create new workflow" footer that invokes this callback. */
  onCreate?: () => void;
  /** Gates the create footer for users without the right permission. Defaults
   *  to true; pass false to hide the footer even when `onCreate` is supplied. */
  canCreate?: boolean;
  /** Telemetry callback fired when the user selects a workflow. */
  onWorkflowChange?: (workflowName: string) => void;
}

const STATUS_CLASS: Record<WorkflowStatus, keyof ReturnType<typeof usePillStyles>> = {
  running: 'statusRunning',
  succeeded: 'statusSucceeded',
  failed: 'statusFailed',
  draft: 'statusDraft',
  idle: 'statusIdle',
};

const STATUS_LABEL: Record<WorkflowStatus, string> = {
  running: 'Running',
  succeeded: 'Succeeded',
  failed: 'Failed',
  draft: 'Draft',
  idle: 'Idle',
};

/**
 * Workflow leaf pill — the focal element of the header.
 *
 * Displays the current workflow's name, an inline status dot for run state,
 * and opens a dropdown of workflows in the current app for quick switching.
 *
 * The runtime status dot lives on the *trigger* (current workflow's state)
 * but is intentionally absent from list rows — per-row status would re-introduce
 * the icon slot we removed from the other pills' menus.
 */
export const WorkflowLeafPill = ({
  locationName,
  projectName,
  appName,
  workflowName,
  workflows,
  currentStatus,
  tenantId,
  onCreate,
  canCreate = true,
  onWorkflowChange,
}: WorkflowLeafPillProps) => {
  const styles = usePillStyles();
  const navigate = useNavigate();
  const requestNodeCodeNavigation = useNodeEditorStore(
    (state) => state.requestNodeCodeNavigation,
  );

  const currentDisplayName = extractWorkflowName(workflowName);

  const getDisplayName = useCallback(
    (w: WorkflowSummary) => extractWorkflowName(w.name),
    [],
  );
  const isCurrentWorkflow = useCallback(
    (w: WorkflowSummary) => extractWorkflowName(w.name) === currentDisplayName,
    [currentDisplayName],
  );
  const {
    search,
    setSearch,
    onMenuOpenChange,
    visible: visibleWorkflows,
    isSearching,
  } = useMenuList(workflows, getDisplayName, isCurrentWorkflow);

  const handleWorkflowClick = useCallback(
    (wf: WorkflowSummary) => {
      const newDisplayName = extractWorkflowName(wf.name);
      const sameWorkflow = newDisplayName === currentDisplayName;
      // Always navigate, even when re-selecting the current workflow.
      // Re-selecting uses replace:true to avoid polluting browser history,
      // while still closing the menu and matching the hyperlink affordance
      // applied to the current row via `highlightCurrentAsLink`. The
      // telemetry callback still skips redundant "changed" events.
      const targetUrl = paths.appWorkflowDesigner(
        locationName,
        projectName,
        appName,
        newDisplayName,
        tenantId,
      );
      if (blockHeaderActionForCodeIssue()) return;
      if (
        requestNodeCodeNavigation(targetUrl, {
          ...(sameWorkflow && { replace: true }),
        })
      ) {
        return;
      }
      if (!sameWorkflow) {
        onWorkflowChange?.(newDisplayName);
      }
      if (sameWorkflow) {
        navigate(targetUrl, { replace: true });
        return;
      }
      navigate(targetUrl);
    },
    [
      currentDisplayName,
      locationName,
      projectName,
      appName,
      tenantId,
      navigate,
      onWorkflowChange,
      requestNodeCodeNavigation,
    ],
  );
  const handleCreate = useCallback(() => {
    if (
      blockHeaderActionForCodeIssue({
        includeNodeCode: true,
        title: 'Apply code changes before creating a workflow',
      })
    ) return;
    onCreate?.();
  }, [onCreate]);

  const statusAriaLabel = currentStatus
    ? `, status ${STATUS_LABEL[currentStatus]}`
    : '';

  return (
    <Menu onOpenChange={onMenuOpenChange}>
      <MenuTrigger disableButtonEnhancement>
        <button
          type="button"
          className={styles.leafPill}
          aria-label={`Current workflow: ${currentDisplayName}${statusAriaLabel}. Click to switch.`}
          aria-current="page"
        >
          <span className={styles.pillPrefix} aria-hidden="true">Workflow:</span>
          <span className={styles.pillLabel}>{currentDisplayName}</span>
          {currentStatus && (
            <span
              className={mergeClasses(styles.statusDot, styles[STATUS_CLASS[currentStatus]])}
              aria-hidden="true"
            />
          )}
          <ChevronDown12Regular className={styles.pillChevron} aria-hidden="true" />
        </button>
      </MenuTrigger>
      <PillMenu
        sectionTitle="Workflows"
        search={search}
        setSearch={setSearch}
        searchPlaceholder="Search workflows…"
        searchAriaLabel="Search workflows"
        items={visibleWorkflows}
        getKey={(wf) => wf.name}
        getLabel={getDisplayName}
        isCurrent={isCurrentWorkflow}
        onSelect={handleWorkflowClick}
        isSearching={isSearching}
        emptyCopy="No workflows in this app"
        noMatchCopy="No matching workflows"
        highlightCurrentAsLink
        {...(canCreate && onCreate
          ? {
              viewAllLabel: 'Create new workflow',
              viewAllIcon: <Add16Regular aria-hidden="true" />,
              onViewAll: handleCreate,
            }
          : {})}
      />
    </Menu>
  );
};
